/**
 * Correcting a set you already logged.
 *
 * Mistyping happens constantly — 22 for 225, 5 for 50, the wrong box entirely
 * with cold hands. Until now the only repair was Remove and re-log, which loses
 * the set's position in the workout and its note along with the typo.
 *
 * The inputs mirror the log form's, including which ones appear: a plank shows a
 * time field and no weight, because `measures` decides both here and there. What
 * it deliberately does NOT show is `rpe` or `is_failure`, which only voice
 * logging can set — the patch never names them, so they survive an edit rather
 * than being quietly wiped by a form with no box for them.
 *
 * Save is disabled until something has actually changed. See lib/setEdit: the
 * kg↔lb round trip is lossy, so a no-op save is not a no-op unless it is made
 * one on purpose.
 */

import { useState } from 'react'

import {
  draftFromSet,
  draftIsComplete,
  patchFromDraft,
  type EditableSet,
  type Measure,
} from '../lib/setEdit'
import { maskDuration, normalizeDuration, parseDuration,
         type DistanceUnit, type Unit } from '../lib/units'

export function SetEditor({
  set,
  measures,
  unit,
  du = 'km',
  addedLoad = false,
  onSave,
  onCancel,
}: {
  set: EditableSet
  measures: Measure[]
  unit: Unit
  /** The distance unit in force — must match the log form, or an edit
   *  silently rewrites a 40 yd sprint as 40 miles. */
  du?: DistanceUnit
  /** The weight box is a dip belt, not the load. */
  addedLoad?: boolean
  onSave: (patch: Partial<EditableSet>) => void | Promise<void>
  onCancel: () => void
}) {
  // Seeded once. Re-deriving on every render would fight the typing, and the
  // set can't change underneath: saving closes the editor.
  const [draft, setDraft] = useState(() => draftFromSet(set, unit, du))
  const [saving, setSaving] = useState(false)

  const patch = patchFromDraft({ original: set, draft, unit, measures, du })
  const changed = Object.keys(patch).length > 0
  const complete = draftIsComplete(draft, measures)

  const takes = (m: Measure) => measures.includes(m)
  const field = (key: keyof typeof draft) => (value: string) =>
    setDraft((d) => ({ ...d, [key]: value }))

  return (
    <div
      style={{
        margin: '.35rem 0 .5rem',
        padding: '.6rem .7rem',
        background: 'var(--surface-2)',
        borderRadius: 'var(--radius)',
      }}
    >
      <div style={{ display: 'flex', gap: '.6rem' }}>
        {takes('weight') && (
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-weight" style={{ margin: '0 0 .2rem' }}>
              {addedLoad
                ? `Added (${unit})`
                : takes('reps') ? `Weight (${unit})` : `Load (${unit})`}
            </label>
            <input
              id="edit-weight"
              type="number"
              inputMode="decimal"
              step="any"
              value={draft.weight}
              onChange={(e) => field('weight')(e.target.value)}
            />
          </div>
        )}
        {takes('reps') && (
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-reps" style={{ margin: '0 0 .2rem' }}>Reps</label>
            <input
              id="edit-reps"
              type="number"
              inputMode="numeric"
              value={draft.reps}
              onChange={(e) => field('reps')(e.target.value)}
            />
          </div>
        )}
        {takes('distance') && (
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-distance" style={{ margin: '0 0 .2rem' }}>
              Distance ({du})
            </label>
            <input
              id="edit-distance"
              type="number"
              inputMode="decimal"
              step="any"
              value={draft.distance}
              onChange={(e) => field('distance')(e.target.value)}
            />
          </div>
        )}
        {takes('time') && (
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-duration" style={{ margin: '0 0 .2rem' }}>Time</label>
            {/* Same clock masking as the log form, and for the same reason:
                the keypad this is corrected on has no colon key. */}
            <input
              id="edit-duration"
              type="text"
              inputMode="numeric"
              placeholder="45 or 1:30"
              value={draft.duration}
              onChange={(e) => field('duration')(maskDuration(e.target.value))}
              onBlur={() => field('duration')(normalizeDuration(draft.duration))}
            />
          </div>
        )}
      </div>

      {takes('time') && draft.duration.trim() !== '' && parseDuration(draft.duration) === null && (
        <div className="muted" style={{ marginTop: '.3rem', fontSize: '.85rem' }}>
          Enter seconds (45) or minutes:seconds (1:30).
        </div>
      )}

      <input
        type="text"
        placeholder="Note — felt heavy, left knee, belt on…"
        value={draft.note}
        style={{ marginTop: '.5rem', fontSize: '.9rem' }}
        onChange={(e) => field('note')(e.target.value)}
      />

      <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', margin: '.6rem 0 0' }}>
        <input
          type="checkbox"
          checked={draft.warmup}
          onChange={(e) => setDraft((d) => ({ ...d, warmup: e.target.checked }))}
          style={{ width: 'auto' }}
        />
        Warmup set
      </label>

      {!complete && (
        <div className="muted" style={{ marginTop: '.4rem', fontSize: '.82rem' }}>
          {/* Says which box, rather than just refusing. */}
          {takes('reps') ? 'A set needs its reps.'
            : takes('distance') ? 'A set needs its distance.'
              : takes('time') ? 'A set needs its time.'
                : 'A set needs its load.'}
        </div>
      )}

      <div style={{ display: 'flex', gap: '.4rem', marginTop: '.6rem' }}>
        <button
          style={{ marginTop: 0, padding: '.3rem .8rem', minHeight: 0, fontSize: '.85rem',
                   whiteSpace: 'nowrap' }}
          disabled={!changed || !complete || saving}
          onClick={async () => {
            setSaving(true)
            try {
              await onSave(patch)
            } finally {
              // The editor usually unmounts on save. Resetting anyway means a
              // failed write leaves a usable form instead of a dead button.
              setSaving(false)
            }
          }}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
        <button
          className="secondary"
          style={{ marginTop: 0, padding: '.3rem .8rem', minHeight: 0, fontSize: '.85rem',
                   whiteSpace: 'nowrap' }}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </div>
  )
}
